import type { FC, ReactNode } from 'react'
import type { Project } from '@/types'

import { useRef } from 'react'
import Link from 'next/link'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { gsap, MOTION, ScrollTrigger, useGSAP } from '@/lib/motion'
import { scrollToTarget } from '@/lib/smooth-scroll'
import { QuoteLink } from '@/components/generals/quote-link'

const VARIANTS = [
    { frame: 'lg:h-[62vh] lg:w-[46.5vh]', item: 'lg:self-center' },
    { frame: 'lg:h-[48vh] lg:w-[36vh]', item: 'lg:self-end lg:mb-[8vh]' },
    { frame: 'lg:h-[56vh] lg:w-[42vh]', item: 'lg:self-start lg:mt-[15vh]' },
    { frame: 'lg:h-[64vh] lg:w-[48vh]', item: 'lg:self-center' },
    { frame: 'lg:h-[50vh] lg:w-[37.5vh]', item: 'lg:self-start lg:mt-[17vh]' },
    { frame: 'lg:h-[58vh] lg:w-[43.5vh]', item: 'lg:self-end lg:mb-[5vh]' }
]

interface WorkItemProps {
    project: Project
    index: number
}

const WorkItem: FC<WorkItemProps> = ({ project, index }): ReactNode => {
    const variant = VARIANTS[index % VARIANTS.length]
    const number = String(index + 1).padStart(2, '0')

    const frame = (
        <span
            data-frame=''
            className={cn(
                'relative block h-[96vw] w-[72vw] overflow-hidden bg-ink-soft sm:h-[64vw] sm:w-[48vw]',
                variant.frame
            )}
        >
            {project.screenshot && (
                <img
                    data-shot=''
                    data-shot-pan=''
                    src={project.screenshot}
                    alt={project.screenshotAlt ?? ''}
                    loading='lazy'
                    decoding='async'
                    className='absolute inset-0 size-full object-cover object-top transition-[filter] duration-700 group-hover:brightness-110'
                />
            )}

            <span aria-hidden='true' className='pointer-events-none absolute inset-0 ring-1 ring-inset ring-paper/10' />
        </span>
    )

    return (
        <article
            data-work-item=''
            aria-labelledby={`work-${project.id}`}
            className={cn('group relative shrink-0 snap-start', variant.item)}
        >
            {project.demoUrl ? (
                <a
                    href={project.demoUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    tabIndex={-1}
                    aria-hidden='true'
                    data-cursor="Preview"
                    className='block'
                >
                    {frame}
                </a>
            ) : frame}

            <div className='mt-4 flex items-start justify-between gap-4'>
                <div className='min-w-0'>
                    <p className='label text-haze'>{number}</p>

                    <h3 id={`work-${project.id}`} className='mt-1.5 text-lg font-semibold leading-tight tracking-[-0.01em] md:text-xl'>
                        <span data-mdlfy-cms={`projects:${project.id}:title`}>{project.title}</span>
                    </h3>

                    <p className='label mt-1.5 text-haze'>
                        <span data-mdlfy-cms={`projects:${project.id}:industry`}>{project.industry}</span>
                    </p>
                </div>

                {project.demoUrl && (
                    <a
                        href={project.demoUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='label inline-flex shrink-0 items-center gap-1.5 pt-6 text-paper transition-colors hover:text-signal'
                    >
                        Live preview
                        <ArrowUpRight aria-hidden='true' className='size-3.5' />
                        <span className='sr-only'> of this design, opens in a new tab</span>
                    </a>
                )}
            </div>
        </article>
    )
}

interface WorkSectionProps {
    projects: Array<Project>
    total: number
}

/** Chapter six: the gallery runs sideways while every design scrolls through its own page. */
export const WorkSection: FC<WorkSectionProps> = ({ projects, total }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const counterRef = useRef<HTMLSpanElement>(null)

    useGSAP(() => {
        const section = ref.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const stage = q('[data-stage]')[0] as HTMLElement
        const viewport = q('[data-viewport]')[0] as HTMLElement
        const track = q('[data-track]')[0] as HTMLElement
        const items = q('[data-work-item]') as Array<HTMLElement>
        const mm = gsap.matchMedia()

        mm.add(`${MOTION.ok} and ${MOTION.desktop}`, () => {
            const distance = () => Math.max(0, track.scrollWidth - viewport.clientWidth)

            gsap.set(viewport, { overflow: 'visible' })
            gsap.set(track, { scrollSnapType: 'none' })

            const slide = gsap.to(track, {
                x: () => -distance(),
                ease: 'none',
                scrollTrigger: {
                    trigger: stage,
                    pin: true,
                    start: 'top top',
                    end: () => `+=${distance()}`,
                    scrub: 0.8,
                    invalidateOnRefresh: true,
                    anticipatePin: 1,
                    onUpdate: self => {
                        const current = Math.min(items.length, Math.max(1, Math.round(self.progress * (items.length - 1)) + 1))

                        if (counterRef.current) counterRef.current.textContent = String(current).padStart(2, '0')
                    }
                }
            })

            gsap.fromTo(q('[data-progress]'), { scaleX: 0 }, {
                scaleX: 1,
                ease: 'none',
                scrollTrigger: { trigger: stage, start: 'top top', end: () => `+=${distance()}`, scrub: 0.8, invalidateOnRefresh: true }
            })

            items.forEach(item => {
                const shot = item.querySelector<HTMLElement>('[data-shot]')
                const frame = item.querySelector<HTMLElement>('[data-frame]')

                if (!shot || !frame) return

                gsap.fromTo(shot, { objectPosition: '50% 0%' }, {
                    objectPosition: '50% 100%',
                    ease: 'none',
                    scrollTrigger: {
                        trigger: item,
                        containerAnimation: slide,
                        start: 'left right',
                        end: 'right left',
                        scrub: true
                    }
                })

                gsap.fromTo(frame, { clipPath: 'inset(12% 0% 12% 0%)' }, {
                    clipPath: 'inset(0% 0% 0% 0%)',
                    ease: 'power2.out',
                    scrollTrigger: {
                        trigger: item,
                        containerAnimation: slide,
                        start: 'left 95%',
                        end: 'left 55%',
                        scrub: true
                    }
                })
            })

            const onFocus = (event: FocusEvent) => {
                const item = (event.target as HTMLElement).closest<HTMLElement>('[data-work-item], [data-work-edge]')
                const trigger = slide.scrollTrigger as ScrollTrigger | undefined

                if (!item || !trigger) return

                const progress = gsap.utils.clamp(0, 1, (item.offsetLeft - viewport.clientWidth * 0.3) / Math.max(1, distance()))

                scrollToTarget(trigger.start + progress * (trigger.end - trigger.start), { immediate: true })
            }

            track.addEventListener('focusin', onFocus)

            return () => track.removeEventListener('focusin', onFocus)
        })

        return () => mm.revert()
    }, { scope: ref, dependencies: [projects.length] })

    return (
        <section
            ref={ref}
            id='examples'
            data-tone='ink'
            data-surface='dark'
            aria-labelledby='examples-title'
            className='relative bg-ink text-paper'
        >
            <div data-stage='' className='relative flex flex-col py-[14vh] lg:h-svh lg:overflow-hidden lg:py-0'>
                <div
                    data-viewport=''
                    className='no-scrollbar overflow-x-auto overscroll-x-contain lg:h-full'
                >
                    <div
                        data-track=''
                        className='flex w-max snap-x snap-mandatory items-start gap-[6vw] px-(--gutter) lg:h-full lg:items-center lg:gap-[4.5vw] lg:pt-(--header-h)'
                    >
                        <div data-work-edge='' className='flex w-[84vw] shrink-0 snap-start flex-col justify-center sm:w-[60vw] lg:w-[34vw]'>
                            <p className='label text-haze'>
                                <span className='mr-3'>04</span>
                                Sample designs
                            </p>

                            <h2
                                id='examples-title'
                                className='display mt-6 text-[clamp(3.4rem,8.2vw,10rem)] leading-[0.84] wdth-75'
                            >
                                Design
                                <br />
                                examples
                            </h2>

                            <p className='mt-8 max-w-[32ch] text-lg leading-relaxed text-haze'>
                                Get some inspiration for your next website! What we create for you will be uniquely you.
                            </p>

                            <div className='mt-10 flex flex-wrap items-center gap-x-8 gap-y-4'>
                                <Link
                                    href='/work'
                                    className='group inline-flex h-12 items-center gap-3 border border-paper/30 px-5 text-sm font-semibold transition-colors hover:border-signal hover:text-signal'
                                >
                                    Browse all {total > 0 ? total : ''} designs
                                    <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                                </Link>

                                <span aria-hidden='true' className='label hidden text-haze lg:inline'>Keep scrolling</span>
                                <span aria-hidden='true' className='label text-haze lg:hidden'>Swipe</span>
                            </div>
                        </div>

                        {projects.map((project, index) => (
                            <WorkItem key={project.id} project={project} index={index} />
                        ))}

                        <div data-work-edge='' className='flex w-[84vw] shrink-0 snap-start flex-col justify-center sm:w-[60vw] lg:w-[34vw] lg:pl-[2vw]'>
                            <p className='display text-[clamp(2.4rem,4.6vw,5.4rem)] leading-[0.92] wdth-87'>
                                What we create for you will be <span className='text-signal'>uniquely you.</span>
                            </p>

                            <div className='mt-10 flex flex-wrap items-center gap-3'>
                                <QuoteLink
                                    service='web-design'
                                    className='group inline-flex h-14 items-center gap-3 bg-signal px-7 font-semibold text-ink transition-colors duration-300 hover:bg-paper'
                                >
                                    Start your project
                                    <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-1' />
                                </QuoteLink>

                                <Link href='/work' className='inline-flex h-14 items-center px-4 text-sm font-semibold underline decoration-paper/40 underline-offset-8 transition-colors hover:text-signal'>
                                    See every example
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <div aria-hidden='true' className='absolute inset-x-0 bottom-0 hidden items-center gap-6 px-(--gutter) pb-8 lg:flex'>
                    <span className='label text-haze'>
                        <span ref={counterRef}>01</span> / {String(projects.length).padStart(2, '0')}
                    </span>

                    <span className='relative h-px flex-1 bg-paper/15'>
                        <span data-progress='' className='absolute inset-0 origin-left bg-signal' />
                    </span>
                </div>
            </div>
        </section>
    )
}
